import React from 'react';

/* ─── Base pulse element ──────────────────────────────────────────────────── */
interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
}) => (
  <div
    className={`animate-pulse bg-gray-200 rounded-lg ${className}`}
    style={{ width, height }}
  />
);

/* ─── Card skeleton – for grid views (Employees, Positions, Shifts…) ───────── */
interface SkeletonCardProps {
  /** Show avatar circle at top (employee cards) */
  avatar?: boolean;
  lines?: number;
  className?: string;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  avatar = false,
  lines = 3,
  className = '',
}) => (
  <div className={`surface-card p-4 space-y-3 ${className}`}>
    {avatar && (
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-gray-200 animate-pulse flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton height="14px" width="60%" />
          <Skeleton height="12px" width="40%" />
        </div>
      </div>
    )}
    {!avatar && <Skeleton height="14px" width="70%" />}
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} height="12px" width={`${85 - i * 15}%`} />
    ))}
    <div className="flex gap-2 pt-2">
      <Skeleton height="32px" className="flex-1" />
      <Skeleton height="32px" width="40px" />
    </div>
  </div>
);

/* ─── Row skeleton – for list views (Shifts, Managers, Contracts…) ──────────── */
interface SkeletonRowProps {
  avatar?: boolean;
  lines?: number;
  className?: string;
}

export const SkeletonRow: React.FC<SkeletonRowProps> = ({
  avatar = false,
  lines = 2,
  className = '',
}) => (
  <div className={`surface-card p-4 flex items-center gap-4 ${className}`}>
    {avatar && (
      <div className="w-10 h-10 rounded-xl bg-gray-200 animate-pulse flex-shrink-0" />
    )}
    <div className="flex-1 space-y-2">
      <Skeleton height="14px" width="50%" />
      {Array.from({ length: lines - 1 }).map((_, i) => (
        <Skeleton key={i} height="12px" width={`${70 - i * 20}%`} />
      ))}
    </div>
    <Skeleton height="28px" width="72px" className="flex-shrink-0" />
  </div>
);

/* ─── Page header skeleton ───────────────────────────────────────────────────── */
export const SkeletonPageHeader: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`space-y-2 mb-6 ${className}`}>
    <Skeleton height="22px" width="160px" />
    <Skeleton height="13px" width="240px" />
  </div>
);

/* ─── Grid skeleton – renders N SkeletonCards in a grid ─────────────────────── */
interface SkeletonGridProps {
  count?: number;
  avatar?: boolean;
  cols?: string;
}

export const SkeletonGrid: React.FC<SkeletonGridProps> = ({
  count = 6,
  avatar = false,
  cols = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
}) => (
  <div className={`grid ${cols} gap-4`}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} avatar={avatar} />
    ))}
  </div>
);

/* ─── List skeleton – renders N SkeletonRows in a stack ─────────────────────── */
interface SkeletonListProps {
  count?: number;
  avatar?: boolean;
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  count = 4,
  avatar = false,
}) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonRow key={i} avatar={avatar} />
    ))}
  </div>
);
